import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import '@datawizio/web-components/dw-polling-widget.js';
import type { DwPollingTranslate } from '@datawizio/web-components/dw-polling-widget.js';

// models
import { IAppAnnouncements } from './core/models/announcements';
import { ILimitation } from './core/models/limitation';
import { LimitationsCodeNames } from './core/models/limitationsCodeNames';
import { PlatformApplications } from './core/models/platformApplications';
import { SocketGroup } from './core/services/socket.service';

// services
import { DataDogService } from './core/services/data-dog.service';
import { HttpCancelService } from './core/services/http-cancel.service';
import { SessionService } from './core/services/session.service';
import { SocketService } from './core/services/socket.service';
import { DwUsersActivityService } from './core/services/dw-users-activity.service';

import { ConfirmModalComponent } from './core/modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from './core/modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from './core/modals/confirm-modal/models/confirmModalAnswer';


import { SHARED_MODULES } from './shared.';
import { FiltersPanelComponent } from './core/components/filters-panel/filters-panel.component';
import { HeaderComponent } from './core/components/header/header.component';
import { PhCrispButtonComponent } from './core/components/ph-crisp-button/ph-crisp-button.component';
import { PhIconComponent } from './core/components/ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from './core/components/ph-icon-wrapper/ph-icon-wrapper.component';

declare global {
	interface Window {
		$crisp: any;
		CRISP_RUNTIME_CONFIG: any;
		CRISP_TOKEN_ID?: string | number;
		CRISP_WEBSITE_ID: string;
		flutter_inappwebview: any;
		CONFIG: {
			auth: {
				clientId: string;
				clientSecret: string;
				redirectPath: string;
				serviceUrl: string;
			};
			crispWebsiteId: string;
			serverType: 'dev' | 'prod';
		};
	}
}

const APP_ANNOUNCEMENTS_STORAGE_KEY: string = 'closedAnnouncements';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
	imports: [
		SHARED_MODULES,
		FiltersPanelComponent,
		HeaderComponent,
		PhCrispButtonComponent,
		PhIconComponent,
		PhIconWrapperComponent
	],
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
    standalone: true
})
export class AppComponent implements OnInit {

	public announcements: Array<IAppAnnouncements> = [];
	public closedAnnouncements: Array<number> = [];
	public exceededPlanLimitation: boolean = false;
	public exceededPlanogramLimitation: boolean = false;
	public exceededProductLimitation: boolean = false;
	public isFramePage: boolean = false;

	protected get accessToken(): string {
		return this.sessionService.token;
	}

	protected get clientId(): string {
		return String(this.sessionService.clientId ?? '');
	}

	protected readonly translator: DwPollingTranslate = (key, fallback, vars) => {
		const value = this.translate.instant(key, vars);
		return !value || value === key ? fallback : value;
	};

	langChangeSubscription: Subscription;
	limitationsModalShown: boolean = false;
	previousRoute: string = '';

	constructor(
		private dialog: MatDialog,
		private router: Router,
		private translate: TranslateService,
		private dataDogService: DataDogService,
		private httpCancelService: HttpCancelService,
		private sessionService: SessionService,
		private socketService: SocketService,
		private dwUsersActivityService: DwUsersActivityService,
	) {
		this.isFramePage = this.sessionService.isFramePage;
	}

	ngOnInit(): void {
		this.router.events.subscribe((event: Event) => {
			if (event instanceof NavigationStart && this.previousRoute && event.url !== this.previousRoute) {
				this.httpCancelService.cancelPendingRequests();
			}
			if (event instanceof NavigationEnd) {
				this.previousRoute = event.url;
				this.router.onSameUrlNavigation = 'ignore';
			}
		});

		window.addEventListener('beforeunload', () => {
			this.httpCancelService.cancelPendingRequests();
		});

		this.socketService.addSubscription(SocketGroup.Limitations);
		this.socketService.limitationsChanged.subscribe((data: { limitations: Array<ILimitation> }) => {
			this.sessionService.defaults.tariff_plan.limitations = data.limitations;
			for (let key in this.sessionService.limitations) {
				this.sessionService.limitations[key].next(
					data.limitations.find((l: ILimitation) => l.codename == key) || null
				);
			}
		});
		this.sessionService.limitations[LimitationsCodeNames.StorePlans].subscribe((l: ILimitation) => {
			this.exceededPlanLimitation = l && l.used > l.limit;
			this.checkLimitationsModal();
		});
		this.sessionService.limitations[LimitationsCodeNames.Planograms].subscribe((l: ILimitation) => {
			this.exceededPlanogramLimitation = l && l.used > l.limit;
			this.checkLimitationsModal();
		});
		this.sessionService.limitations[LimitationsCodeNames.Products].subscribe((l: ILimitation) => {
			this.exceededProductLimitation = l && l.used > l.limit;
			this.checkLimitationsModal();
		});

		this.announcements = this.sessionService.defaults.announcements;
		this.closedAnnouncements = JSON.parse(localStorage.getItem(APP_ANNOUNCEMENTS_STORAGE_KEY) || '[]');

		if (!this.isFramePage) {
			this.sessionService.getDemoExpiresDays();
			this.dataDogService.init();
			this.dwUsersActivityService.geolocationInitialize();

			window.CRISP_WEBSITE_ID = window.CONFIG.crispWebsiteId;

			this.langChangeSubscription = this.translate.onLangChange.subscribe(async (value: LangChangeEvent) => {
				let lang: string = value.lang;
				if (lang === 'bahasa') lang = 'id';
				if (window.CRISP_RUNTIME_CONFIG && window.CRISP_RUNTIME_CONFIG.locale != lang) {
					window.CRISP_RUNTIME_CONFIG = { locale: lang };
					window.$crisp.push(['do', 'session:reset']);
				}
			});

			window.CRISP_TOKEN_ID = this.sessionService.defaults.user_id;
			let lang: string = this.sessionService.language;
			if (lang === 'bahasa') lang = 'id';
			window.CRISP_RUNTIME_CONFIG = { locale: lang };
			window.$crisp = [
				['set', 'user:nickname', this.sessionService.defaults.fullName],
				['set', 'user:avatar', this.sessionService.defaults.image]
			];

			if (this.sessionService.defaults.email) {
				window.$crisp.push(['set', 'user:email', this.sessionService.defaults.email]);
			}

			window.$crisp.push(['set', 'session:data', [[['Client', this.sessionService.clientName]]]]);
			window.$crisp.push(['safe', true]);

			(function () {
				const doc: Document = window.document,
					script: HTMLScriptElement = doc.createElement('script');

				script.src = 'https://client.crisp.chat/l.js';
				script.async = true;
				doc.getElementsByTagName('head')[0].appendChild(script);
			})();
		}
	}

	private checkLimitationsModal() {
		if (!this.exceededPlanLimitation && !this.exceededPlanogramLimitation && !this.exceededProductLimitation) {
			this.stopLimitationModal();
			return;
		}
		if (this.limitationsModalShown) return;

		this.limitationsModalShown = true;

		this.startLimitationsModal();
	}

	private startLimitationsModal(): void {
		setTimeout(() => {
			if (!this.limitationsModalShown) return;
			this.dialog
				.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
					width: '724px',
					autoFocus: false,
					backdropClass: 'back-drop',
					data: {
						label: 'WELCOME.LIMITATION_MODAL_TITLE',
						secondLabel: 'WELCOME.LIMITATION_MODAL',
						cancelLabel: 'ERROR.TO_SUPPORT',
						confirmLabel: 'COMMON.CHOOSE_TARIFF',
						twoAnswers: true
					}
				})
				.afterClosed()
				.subscribe((result) => {
					if (result.answer == true) {
						window.open(
							this.sessionService.getExternalLink(PlatformApplications.AdminPanel, '/tariff-plans'),
							'_blank'
						);
					}

					if (result.answer == false) {
						window.$crisp.push(['do', 'chat:open']);
					}

					this.startLimitationsModal();
				});
		}, 1000 * 60 * 3);
	}

	private stopLimitationModal(): void {
		this.limitationsModalShown = false;
	}

	public closeAnnouncements(id: number): void {
		this.closedAnnouncements.push(id);
		localStorage.setItem(APP_ANNOUNCEMENTS_STORAGE_KEY, JSON.stringify(this.closedAnnouncements));
	}
}